import { useState } from 'react'
import { useApiQuery, type Column } from 'grabbitbot-legacy-ui'
import { Table } from '../../components/DataTable'
import { projects } from '../../api/fee'
import type { ProjectUser } from '../../api/types'
import { ConfirmButton, Notice, PageTitle, QueryState } from '../../components/common'
import { useProject } from '../../layout/projectContext'
import { useMoneyAction } from '../../lib/useMoneyAction'

const OWNER = 'ROLE_OWNER'
const FULL = 'ROLE_FULL'

/**
 * Port of `ProjectUserAccessComponent` (`projects/:projectId/users`, admin
 * only): invite a user by e-mail, grant/revoke the admin role (`ROLE_FULL`),
 * remove a user. The owner row cannot be changed. Legacy removed a user on
 * the first click; the port asks once inline (no behaviour change for the
 * backend call).
 */
export function ProjectUserAccessPage() {
  const { projectId, project } = useProject()
  const q = useApiQuery(() => projects.users(projectId), [projectId])
  const [email, setEmail] = useState('')
  const actions = useMoneyAction()

  const invite = async () => {
    const u = email.trim()
    if (!u) return
    const r = await actions.run('invite', u, () => projects.addUser(projectId, u))
    if (r !== undefined) {
      setEmail('')
      q.refetch()
    }
  }
  const act = (key: string, fn: () => Promise<unknown>) => actions.run(key, key, fn, () => q.refetch())

  const columns: Column<ProjectUser>[] = [
    {
      key: 'username',
      label: 'Email',
      format: (_v, u) => (
        <>
          {u.username}
          {u.roles.includes(OWNER) && <span className="portal-muted-small">(owner)</span>}
        </>
      ),
    },
    {
      key: 'level',
      label: 'User level',
      sortValue: (u) => (u.roles.includes(OWNER) ? 2 : u.roles.includes(FULL) ? 1 : 0),
      format: (_v, u) =>
        u.roles.includes(OWNER) ? (
          <span className="portal-chip fs-chip--danger">Owner</span>
        ) : u.roles.includes(FULL) ? (
          <span className="portal-inline-actions">
            <span className="portal-chip fs-chip--warning">Admin</span>
            <button
              type="button"
              className="fs-link-button"
              disabled={actions.isBusy(`role-${u.username}`)}
              onClick={() => void act(`role-${u.username}`, () => projects.removeRole(projectId, u.username, FULL))}
            >
              revoke admin role
            </button>
          </span>
        ) : (
          <span className="portal-inline-actions">
            <span className="portal-chip">User</span>
            <button
              type="button"
              className="fs-link-button"
              disabled={actions.isBusy(`role-${u.username}`)}
              onClick={() => void act(`role-${u.username}`, () => projects.addRole(projectId, u.username, FULL))}
            >
              add admin role
            </button>
          </span>
        ),
    },
    {
      key: 'actions',
      label: '',
      sortable: false,
      format: (_v, u) =>
        u.roles.includes(OWNER) ? null : (
          <>
            <ConfirmButton
              label="Remove"
              pendingText={`Remove ${u.username} from this project?`}
              className="portal-button-danger"
              busy={actions.isBusy(`del-${u.username}`)}
              onConfirm={() => void act(`del-${u.username}`, () => projects.removeUser(projectId, u.username))}
            />
            <Notice feedback={actions.feedbackFor(`del-${u.username}`) ?? actions.feedbackFor(`role-${u.username}`)} />
          </>
        ),
    },
  ]

  return (
    <div className="portal-card">
      <PageTitle title={`User access — ${project.name}`} />
      <form
        className="portal-toolbar"
        onSubmit={(e) => {
          e.preventDefault()
          void invite()
        }}
      >
        <input type="email" aria-label="Invite by e-mail" placeholder="user@example.com" value={email} onChange={(e) => setEmail(e.target.value)} />
        <button type="submit" disabled={actions.isBusy('invite') || !email.trim()}>
          Invite
        </button>
      </form>
      <Notice feedback={actions.feedbackFor('invite')} />
      <QueryState loading={q.loading} error={q.error}>
        <div className="portal-table-scroll">
          <Table columns={columns} rows={q.data ?? []} rowKey={(u) => u.username} initialSort={{ key: 'username', dir: 'asc' }} />
        </div>
      </QueryState>
    </div>
  )
}
